/**
 * Gate Pass Form — Digi Sahyadri WRMS
 * Matches the physical Gate Pass Form exactly
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPaperPlane, FaUserFriends } from 'react-icons/fa';
import WRMSFormHeader from '../shared/WRMSFormHeader';
import SignatureFooter from '../shared/SignatureFooter';
import { submitRequest } from '../../services/wrmsService';
import { playNotificationSound } from '../../../utils/soundService';
import { getEmployeesByDepartment } from '../../../services/firebase/employeeService';
import { getApprovalChain, getDeptGroup, getStageLabelForRole } from '../../utils/ApprovalRouter';

const today = () => new Date().toISOString().split('T')[0];

const GatePassForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [deptEmployees, setDeptEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Fetch department colleagues for handover dropdown
  useEffect(() => {
    const dept = user?.department;
    if (!dept) return;
    setLoadingEmployees(true);
    getEmployeesByDepartment(dept)
      .then(res => {
        const list = (res.data || []).filter(emp => {
          const empEmail = emp.personal?.email || emp.email || '';
          return empEmail.toLowerCase() !== (user?.email || '').toLowerCase();
        });
        setDeptEmployees(list);
      })
      .catch(() => setDeptEmployees([]))
      .finally(() => setLoadingEmployees(false));
  }, [user?.department, user?.email]);

  const [form, setForm] = useState({
    date: today(),
    returnDate: today(),
    isHalfDay: false,
    timeOut: '',
    expectedTimeIn: '',
    reason: '',
    currentResponsibility: '',
    handoverTo: '',
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.timeOut || !form.expectedTimeIn || !form.reason) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitRequest('gate_pass', form, user);
      playNotificationSound();
      toast.success(`Gate pass submitted! ID: ${result.requestId}`);
      navigate('/wrms');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const deptGroup = getDeptGroup(user?.department, user?.role);
  const chain = getApprovalChain('gate_pass', deptGroup);
  
  const signatories = chain.map(step => ({
    title: step.role === 'employee' ? 'Signature of Applicant' : `Signature of ${getStageLabelForRole(step.role)}`,
    name: step.role === 'employee' ? (user?.employeeName || user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '') : null,
    date: step.role === 'employee' ? new Date() : null,
  }));

  return (
    <div>
      <WRMSFormHeader formTitle="Gate Pass Form" formSubtitle="Digi Sahyadri — Request System" />

      <div className="wrms-form-body">
        <form onSubmit={handleSubmit}>
          {/* Auto-populated Section */}
          <div className="wrms-section-label">Employee Details (Auto-populated)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div className="wrms-form-group">
              <label>Name</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.employeeName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || ''} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Team ID</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.employeeCode || user?.teamId || '—'} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Designation</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.designation || '—'} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Department</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.department || '—'} readOnly />
            </div>
          </div>

          {/* Gate Pass Details */}
          <div className="wrms-section-label">Gate Pass Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Date</label>
              <input type="date" className="wrms-form-control form-control"
                value={form.date}
                onChange={e => handleChange('date', e.target.value)} />
            </div>
            <div className="wrms-form-group">
              <label>To Date / Return Date</label>
              <input type="date" className="wrms-form-control form-control"
                value={form.returnDate}
                min={form.date}
                onChange={e => handleChange('returnDate', e.target.value)} />
            </div>
          </div>

          {/* Half Day Toggle */}
          <div className="wrms-form-group" style={{ marginBottom: '20px' }}>
            <label>Half Day?</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
              <label className={`wrms-radio-btn ${form.isHalfDay ? 'selected' : ''}`}
                onClick={() => handleChange('isHalfDay', !form.isHalfDay)}>
                {form.isHalfDay ? 'Yes, Half Day' : 'Half Day'}
              </label>
              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                {form.isHalfDay ? 'Marked as Half Day' : 'Full absence'}
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Time Out <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="time" className="wrms-form-control form-control"
                value={form.timeOut}
                onChange={e => handleChange('timeOut', e.target.value)} required />
              <small style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px', display: 'block' }}>When do you plan to leave?</small>
            </div>
            <div className="wrms-form-group">
              <label>Time In / Expected Return Time <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="time" className="wrms-form-control form-control"
                value={form.expectedTimeIn}
                onChange={e => handleChange('expectedTimeIn', e.target.value)} required />
              <small style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px', display: 'block' }}>Expected return time</small>
            </div>
          </div>

          {/* Reason */}
          <div className="wrms-form-group" style={{ marginBottom: '20px' }}>
            <label>Reason for Leave <span style={{ color: '#dc2626' }}>*</span></label>
            <textarea className="wrms-form-control form-control" rows={3}
              placeholder="Please describe the reason for this gate pass..."
              value={form.reason}
              onChange={e => handleChange('reason', e.target.value)} required />
          </div>

          {/* Handover */}
          <div className="wrms-section-label">Handover Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' }}>
            <div className="wrms-form-group">
              <label>Current Responsibility</label>
              <textarea className="wrms-form-control form-control" rows={3}
                placeholder="List your responsibilities..."
                value={form.currentResponsibility}
                onChange={e => handleChange('currentResponsibility', e.target.value)} />
            </div>
            <div className="wrms-form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaUserFriends size={13} style={{ color: '#4f46e5' }} />
                Current Responsibility Hand Over To
              </label>
              {loadingEmployees ? (
                <div style={{ padding: '10px', fontSize: '0.82rem', color: '#64748b' }}>Loading department employees...</div>
              ) : deptEmployees.length > 0 ? (
                <select
                  className="wrms-form-control form-control"
                  value={form.handoverToId || form.handoverTo}
                  onChange={e => {
                    const val = e.target.value;
                    const selectedEmp = deptEmployees.find(emp => emp.id === val || (emp.personal?.fullName || emp.firstName) === val);
                    if (selectedEmp) {
                      const name = selectedEmp.personal?.fullName ||
                        `${selectedEmp.personal?.firstName || ''} ${selectedEmp.personal?.lastName || ''}`.trim() ||
                        selectedEmp.firstName || 'Unknown';
                      setForm(prev => ({
                        ...prev,
                        handoverTo: name,
                        handoverToId: selectedEmp.id,
                        handoverToEmail: selectedEmp.personal?.email || selectedEmp.email || '',
                        handoverToTeamId: selectedEmp.job?.employeeId || selectedEmp.employeeCode || selectedEmp.teamId || '',
                      }));
                    } else {
                      setForm(prev => ({ ...prev, handoverTo: val, handoverToId: '', handoverToEmail: '', handoverToTeamId: '' }));
                    }
                  }}
                  style={{ height: '42px' }}
                >
                  <option value="">— Select employee —</option>
                  {deptEmployees.map(emp => {
                    const name = emp.personal?.fullName ||
                      `${emp.personal?.firstName || ''} ${emp.personal?.lastName || ''}`.trim() ||
                      emp.firstName || 'Unknown';
                    const designation = emp.job?.designation || emp.designation || '';
                    return (
                      <option key={emp.id} value={emp.id}>
                        {name}{designation ? ` — ${designation}` : ''}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <input type="text" className="wrms-form-control form-control"
                  placeholder="Enter employee name (no dept colleagues found)"
                  value={form.handoverTo}
                  onChange={e => handleChange('handoverTo', e.target.value)} />
              )}
              {deptEmployees.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                  Showing {deptEmployees.length} active colleague{deptEmployees.length !== 1 ? 's' : ''} from your department · Selected colleague will receive handover request for approval first
                </div>
              )}
            </div>
          </div>

          <SignatureFooter signatories={signatories} />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
            <button type="button" className="wrms-btn-outline"
              onClick={() => navigate('/wrms')} style={{ padding: '10px 24px' }}>
              Cancel
            </button>
            <button type="submit" className="wrms-btn-primary" disabled={submitting}>
              <FaPaperPlane size={13} />
              {submitting ? 'Submitting...' : 'Submit Gate Pass Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GatePassForm;
